import React from "react";
import { Link } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { createJob, deleteJob, deleteJobsBulk, listJobs } from "../../api/jobs";

export default function JobsDashboard() {
  const [jobs, setJobs] = React.useState([]);
  const [query, setQuery] = React.useState("");
  const [newJobName, setNewJobName] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [busyDelete, setBusyDelete] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState(() => new Set());

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await listJobs({ limit: 200, offset: 0 });
      setJobs(rows);
    } catch (e) {
      setError(e?.message || "Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    const valid = new Set(jobs.map((j) => String(j?.id || "")));
    setSelectedIds((prev) => {
      const next = new Set();
      prev.forEach((id) => {
        if (valid.has(id)) next.add(id);
      });
      return next;
    });
  }, [jobs]);

  const handleCreate = async () => {
    setBusy(true);
    setError("");
    try {
      await createJob(newJobName);
      setNewJobName("");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to create job");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteOne = async (jobId) => {
    const id = String(jobId || "").trim();
    if (!id) return;
    const ok = window.confirm("Delete this job? This removes related data tied to the job.");
    if (!ok) return;

    setBusyDelete(true);
    setError("");
    try {
      await deleteJob(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to delete job");
    } finally {
      setBusyDelete(false);
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 1) return;
    const ok = window.confirm(`Delete ${ids.length} selected job(s)? This removes related data tied to each job.`);
    if (!ok) return;

    setBusyDelete(true);
    setError("");
    try {
      await deleteJobsBulk(ids);
      setSelectedIds(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to delete selected jobs");
    } finally {
      setBusyDelete(false);
    }
  };

  const filtered = jobs.filter((j) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return String(j?.name || "").toLowerCase().includes(q) || String(j?.id || "").toLowerCase().includes(q);
  });

  const filteredIds = filtered.map((j) => String(j?.id || "")).filter(Boolean);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  const toggleSelectAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        filteredIds.forEach((id) => next.add(id));
      } else {
        filteredIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const toggleSelectRow = (jobId, checked) => {
    const id = String(jobId || "");
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Jobs</h1>
          <p className="mt-1 text-sm text-slate-500">Create, browse, and delete estimate jobs.</p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[260px]"
              placeholder="Search jobs by name or id"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Button variant="secondary" onClick={refresh} disabled={loading || busy || busyDelete}>
              Refresh
            </Button>
            <Button
              variant="secondary"
              onClick={handleDeleteSelected}
              disabled={busyDelete || selectedIds.size < 1}
            >
              {busyDelete ? "Deleting..." : `Delete Selected (${selectedIds.size})`}
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[260px]"
              placeholder="New job name"
              value={newJobName}
              onChange={(e) => setNewJobName(e.target.value)}
            />
            <Button onClick={handleCreate} disabled={busy || busyDelete}>
              {busy ? "Creating..." : "Create Job"}
            </Button>
            <Link to="/app/build">
              <Button variant="ghost">Open Build Wizard</Button>
            </Link>
            <Link to="/mitigation">
              <Button variant="secondary">Open Mitigation Station</Button>
            </Link>
            <Link to="/app/personal-property">
              <Button variant="secondary">Open Contents Corner</Button>
            </Link>
          </div>

          {loading ? <p className="text-sm text-slate-500">Loading jobs...</p> : null}
          {!loading ? (
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-slate-600">
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
                        aria-label="Select all filtered jobs"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-slate-600">Name</th>
                    <th className="px-3 py-2 text-left text-slate-600">Job ID</th>
                    <th className="px-3 py-2 text-left text-slate-600">CRM</th>
                    <th className="px-3 py-2 text-left text-slate-600">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length < 1 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-slate-500">
                        No jobs found.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((job, idx) => {
                      const id = String(job?.id || "");
                      const isSelected = selectedIds.has(id);
                      return (
                        <tr key={String(job?.id || `row-${idx}`)} className="border-t border-slate-100">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => toggleSelectRow(id, e.target.checked)}
                              aria-label={`Select job ${id}`}
                            />
                          </td>
                          <td className="px-3 py-2 text-slate-900">{String(job?.name || "").trim() || "(untitled)"}</td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-600">{id}</td>
                          <td className="px-3 py-2">
                            <Link to={`/app/crm?jobId=${encodeURIComponent(id)}`}>
                              <Button size="sm" variant="secondary">Open CRM</Button>
                            </Link>
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteOne(id)}
                              disabled={busyDelete}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
